/**
 * Admin Promotions API module — CON-PROMO-005 / CON-PROMO-006.
 *
 * Typed functions for admin-facing automatic promotion endpoints.
 * Containers and Redux thunks import from this module only.
 *
 * Endpoints covered:
 *   GET    /api/v1/admin/promotions              — paginated list with filters
 *   GET    /api/v1/admin/promotions/:id          — single promotion for edit form
 *   POST   /api/v1/admin/promotions              — create promotion
 *   PUT    /api/v1/admin/promotions/:id          — full update
 *   PATCH  /api/v1/admin/promotions/:id/toggle   — flip is_active flag
 *   PUT    /api/v1/admin/promotions/reorder      — batch priority reorder
 *
 * Promotions are never deleted — the platform refuses it deliberately, because a
 * promotion that has already discounted an order has to stay on the record.
 * Deactivate with the toggle endpoint instead.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  CreatePromotionPayload,
  FetchPromotionsParams,
  PromotionDTO,
  PromotionListResponse,
  ReorderPromotionsPayload,
  UpdatePromotionPayload,
} from '@dfq/shared/types/promotions';

// ---------------------------------------------------------------------------
// GET /api/v1/admin/promotions
// ---------------------------------------------------------------------------

export async function fetchAdminPromotions(
  params: FetchPromotionsParams = {},
): Promise<PromotionListResponse> {
  try {
    const res = await apiClient.get<ApiEnvelope<PromotionListResponse>>(
      '/admin/promotions',
      { params },
    );
    const data = unwrap(res.data, res.status);
    // Platform envelope returns pagination under `meta.pagination` in
    // snake_case; some MSW test fixtures put it under `data.pagination` in
    // camelCase. Project either source into the response so the slice's
    // `state.pagination = payload.pagination` lands a usable object instead
    // of undefined.
    const wirePag = (res.data.meta as
      | { pagination?: { total: number; page: number; per_page: number; last_page: number } }
      | undefined)?.pagination;
    return {
      ...data,
      pagination:
        data.pagination ??
        (wirePag
          ? {
              total: wirePag.total,
              page: wirePag.page,
              perPage: wirePag.per_page,
              lastPage: wirePag.last_page,
            }
          : null),
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/promotions/:id
// ---------------------------------------------------------------------------

export async function fetchAdminPromotion(id: number): Promise<PromotionDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ promotion: PromotionDTO }>>(
      `/admin/promotions/${id}`,
    );
    return unwrap(res.data, res.status).promotion;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/promotions
// ---------------------------------------------------------------------------

export async function createAdminPromotion(
  payload: CreatePromotionPayload,
): Promise<PromotionDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ promotion: PromotionDTO }>>(
      '/admin/promotions',
      payload,
    );
    return unwrap(res.data, res.status).promotion;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PUT /api/v1/admin/promotions/:id  (status toggle / full update)
// ---------------------------------------------------------------------------

export async function updateAdminPromotion(
  id: number,
  payload: UpdatePromotionPayload,
): Promise<PromotionDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ promotion: PromotionDTO }>>(
      `/admin/promotions/${id}`,
      payload,
    );
    return unwrap(res.data, res.status).promotion;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/admin/promotions/:id/toggle  (flip is_active)
// ---------------------------------------------------------------------------

export async function toggleAdminPromotion(id: number): Promise<PromotionDTO> {
  try {
    const res = await apiClient.patch<ApiEnvelope<{ promotion: PromotionDTO }>>(
      `/admin/promotions/${id}/toggle`,
    );
    return unwrap(res.data, res.status).promotion;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PUT /api/v1/admin/promotions/reorder  (batch priority update)
// ---------------------------------------------------------------------------

export async function reorderAdminPromotions(
  payload: ReorderPromotionsPayload,
): Promise<void> {
  try {
    await apiClient.put('/admin/promotions/reorder', payload);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Namespaced client object (mirrors pattern from other admin API modules)
// ---------------------------------------------------------------------------

export const promotionsAdminApiClient = {
  fetchPromotions: fetchAdminPromotions,
  fetchPromotion: fetchAdminPromotion,
  createPromotion: createAdminPromotion,
  updatePromotion: updateAdminPromotion,
  togglePromotion: toggleAdminPromotion,
  reorderPromotions: reorderAdminPromotions,
};
